import { useState, useEffect } from 'react';
import { getTarifarios, getTiposTarifario } from '@shared/helpers/api/mantenedores/tarifarios';
import { getComunas, getRegiones, getServiciosSpread, getClientes, getTMS } from '@shared/helpers/api/mantenedores/generales';

/**
 * Hook para manejar el mantenedor de tarifarios completo
 */
export const useTarifariosMantenedor = (user, permisos) => {
    const [tarifarios, setTarifarios] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);

    // Filtros
    const [tipoTarifarioId, setTipoTarifarioId] = useState('');
    const [comunaId, setComunaId] = useState('');
    const [servicioId, setServicioId] = useState('');
    const [clienteId, setClienteId] = useState('');
    const [regionId, setRegionId] = useState('');
    const [tmsId, setTmsId] = useState('');

    // Datos para dropdowns
    const [tiposTarifario, setTiposTarifario] = useState([]);
    const [comunas, setComunas] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [regiones, setRegiones] = useState([]);
    const [tms, setTMS] = useState([]);

    // Cargar datos iniciales para dropdowns
    useEffect(() => {
        const loadInitialData = async () => {
            if (!user) return;

            try {
                // Primero cargar tipos de tarifario para establecer el default
                const tiposRes = await getTiposTarifario(user);
                
                if (tiposRes.success && tiposRes.data.length > 0) {
                    setTiposTarifario(tiposRes.data);
                    
                    // Establecer "ingreso" como tipo predeterminado si no hay uno seleccionado
                    if (!tipoTarifarioId) {
                        const tipoIngreso = tiposRes.data.find(tipo => tipo.tipo === 'ingreso');
                        if (tipoIngreso) {
                            setTipoTarifarioId(tipoIngreso.id);
                        }
                    }
                }

                // Luego cargar el resto de datos en paralelo
                const [comunasRes, serviciosRes, clientesRes, regionesRes, tmsRes] = await Promise.all([
                    getComunas(user),
                    getServiciosSpread(user),
                    getClientes(user),
                    getRegiones(user),
                    getTMS(user)
                ]);
                
                setComunas(comunasRes.success ? comunasRes.data : []);
                setServicios(serviciosRes.success ? serviciosRes.data : []);
                setClientes(clientesRes.success ? clientesRes.data : []);
                setRegiones(regionesRes.success ? regionesRes.data : []);
                setTMS(tmsRes.success ? tmsRes.data : []);
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error);
            }
        };

        loadInitialData();
    }, [user]); // Removido tipoTarifarioId de las dependencias

    // Cargar tarifarios
    useEffect(() => {
        const loadTarifarios = async () => {
            // No cargar si no tenemos user o tipoTarifarioId
            if (!user || !tipoTarifarioId) return;

            setLoading(true);
            setError(null);

            try {
                const params = {
                    pageSize,
                    pageNumber,
                    tipo_tarifario_id: tipoTarifarioId,
                    comuna_id: comunaId || undefined,
                    servicio_id: servicioId || undefined,
                    cliente_id: clienteId || undefined,
                    region_id: regionId || undefined,
                    tms_id: tmsId || undefined
                };

                const response = await getTarifarios(user, params);

                if (response.success) {
                    setTarifarios(response.data);
                    setTotal(response.total);
                } else {
                    setTarifarios([]);
                    setTotal(0);
                    setError(response.error);
                }
            } catch (error) {
                console.error('Error al cargar tarifarios:', error);
                setTarifarios([]);
                setTotal(0);
                setError('Error al cargar tarifarios');
            } finally {
                setLoading(false);
            }
        };

        loadTarifarios();
    }, [user, pageSize, pageNumber, tipoTarifarioId, comunaId, servicioId, clienteId, regionId, tmsId]);

    // Handlers para filtros y paginación
    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPageNumber(1);
    };

    const handleTipoTarifarioChange = (value) => {
        setTipoTarifarioId(value);
        setPageNumber(1);
        // Si no es "egreso", limpiar cliente
        if (value && tiposTarifario.find(t => t.id == value)?.tipo !== 'egreso') {
            setClienteId('');
        }
    };

    const handleComunaChange = (value) => {
        setComunaId(value);
        setPageNumber(1);
    };

    const handleServicioChange = (value) => {
        setServicioId(value);
        setPageNumber(1);
    };

    const handleClienteChange = (value) => {
        setClienteId(value);
        setPageNumber(1);
    };

    const handleRegionChange = (value) => {
        setRegionId(value);
        setPageNumber(1);
    };

    const handleTmsChange = (value) => {
        setTmsId(value);
        setPageNumber(1);
    };

    const handleSearch = () => {
        setPageNumber(1);
        // El useEffect se disparará automáticamente
    };

    // Función para refrescar los datos (útil después de actualizaciones)
    const refreshData = () => {
        // Alternativamente, podemos crear un estado de refresh
        const loadTarifarios = async () => {
            if (!user || !tipoTarifarioId) return;

            setLoading(true);
            setError(null);

            try {
                const params = {
                    pageSize,
                    pageNumber,
                    tipo_tarifario_id: tipoTarifarioId,
                    comuna_id: comunaId || undefined,
                    servicio_id: servicioId || undefined,
                    cliente_id: clienteId || undefined,
                    region_id: regionId || undefined,
                    tms_id: tmsId || undefined
                };

                const response = await getTarifarios(user, params);

                if (response.success) {
                    setTarifarios(response.data);
                    setTotal(response.total);
                } else {
                    setTarifarios([]);
                    setTotal(0);
                    setError(response.error);
                }
            } catch (error) {
                console.error('Error al cargar tarifarios:', error);
                setTarifarios([]);
                setTotal(0);
                setError('Error al cargar tarifarios');
            } finally {
                setLoading(false);
            }
        };

        // Ejecutar la carga inmediatamente
        loadTarifarios();
    };

    // Determinar si el filtro de cliente debe estar habilitado
    const isClienteEnabled = () => {
        if (!tipoTarifarioId) return false;
        const tipoSeleccionado = tiposTarifario.find(t => t.id == tipoTarifarioId);
        return tipoSeleccionado?.tipo === 'egreso';
    };

    return {
        tarifarios,
        total,
        loading,
        error,
        pageSize,
        pageNumber,
        tipoTarifarioId,
        tiposTarifario,
        comunaId,
        comunas,
        servicioId,
        servicios,
        clienteId,
        clientes,
        regionId,
        regiones,
        tms,
        tmsId,
        handlePageChange,
        handlePageSizeChange,
        handleTipoTarifarioChange,
        handleComunaChange,
        handleServicioChange,
        handleClienteChange,
        handleRegionChange,
        handleTmsChange,
        handleSearch,
        refreshData,
        isClienteEnabled: isClienteEnabled()
    };
};
